'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function PaceCalculator() {
  const t = useTranslations("tools.paceCalculator");
  const [screenSize, setScreenSize] = useState('mobile');
  const [activeTab, setActiveTab] = useState('basic');

  // Basic calculator state
  const [basicInputs, setBasicInputs] = useState({
    time: '',
    distance: '',
    distanceUnit: 'miles',
    pace: '',
    paceUnit: 'min-per-mile',
    event: ''
  });
  const [basicResults, setBasicResults] = useState<any>(null);

  // Multipoint calculator state
  const [multipointInputs, setMultipointInputs] = useState([
    { distance: '', unit: 'kilometers', time: '' },
    { distance: '', unit: 'kilometers', time: '' },
    { distance: '', unit: 'kilometers', time: '' },
    { distance: '', unit: 'kilometers', time: '' },
    { distance: '', unit: 'kilometers', time: '' }
  ]);
  const [multipointPaceUnit, setMultipointPaceUnit] = useState('min-per-mile');
  const [multipointResults, setMultipointResults] = useState<any>(null);

  // Converter state
  const [converterInputs, setConverterInputs] = useState({
    fromValue: '',
    fromUnit: 'min-per-mile',
    toUnit: 'min-per-km'
  });
  const [converterResult, setConverterResult] = useState('');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  // Utility functions
  const parseTime = (timeStr: string): number => {
    if (!timeStr) return 0;
    const parts = timeStr.split(':').map(p => parseFloat(p) || 0);
    if (parts.length === 1) return parts[0] * 60;
    if (parts.length === 2) return parts[0] * 60 + parts[1];
    if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
    return 0;
  };

  const formatTime = (seconds: number): string => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const convertToMiles = (distance: number, unit: string): number => {
    const conversions: { [key: string]: number } = {
      'miles': 1,
      'kilometers': 0.621371,
      'meters': 0.000621371,
      'yards': 0.000568182
    };
    return distance * conversions[unit];
  };

  const convertToKm = (distance: number, unit: string): number => {
    const conversions: { [key: string]: number } = {
      'miles': 1.60934,
      'kilometers': 1,
      'meters': 0.001,
      'yards': 0.0009144
    };
    return distance * conversions[unit];
  };

  // Event handler
  const handleEventChange = (event: string) => {
    const events: { [key: string]: { distance: number; unit: string } } = {
      'marathon': { distance: 26.2, unit: 'miles' },
      'half-marathon': { distance: 13.1, unit: 'miles' },
      '10k': { distance: 10, unit: 'kilometers' },
      '5k': { distance: 5, unit: 'kilometers' },
      '1k': { distance: 1, unit: 'kilometers' }
    };

    if (event && events[event]) {
      setBasicInputs({
        ...basicInputs,
        event,
        distance: events[event].distance.toString(),
        distanceUnit: events[event].unit
      });
    } else {
      setBasicInputs({ ...basicInputs, event });
    }
  };

  // Basic calculator
  const handleBasicCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    let timeSeconds = parseTime(basicInputs.time);
    let distanceMiles = basicInputs.distance ? convertToMiles(parseFloat(basicInputs.distance), basicInputs.distanceUnit) : 0;
    let paceSeconds = 0;

    if (basicInputs.paceUnit === 'mph' || basicInputs.paceUnit === 'kph') {
      const speed = parseFloat(basicInputs.pace);
      if (speed > 0) {
        if (basicInputs.paceUnit === 'mph') {
          paceSeconds = 3600 / speed;
        } else {
          paceSeconds = 3600 / (speed * 0.621371);
        }
      }
    } else {
      paceSeconds = parseTime(basicInputs.pace);
    }

    let filledCount = 0;
    if (timeSeconds > 0) filledCount++;
    if (distanceMiles > 0) filledCount++;
    if (paceSeconds > 0) filledCount++;

    if (filledCount < 2) {
      alert(t('errorTwoValues'));
      return;
    }

    if (timeSeconds === 0) {
      timeSeconds = paceSeconds * distanceMiles;
    } else if (distanceMiles === 0) {
      distanceMiles = timeSeconds / paceSeconds;
    } else if (paceSeconds === 0) {
      paceSeconds = timeSeconds / distanceMiles;
    }

    const distanceKm = distanceMiles * 1.60934;
    const pacePerMile = paceSeconds;
    const pacePerKm = paceSeconds / 1.60934;
    const speedMph = 3600 / paceSeconds;
    const speedKph = speedMph * 1.60934;

    setBasicResults({
      time: formatTime(timeSeconds),
      distance: `${distanceMiles.toFixed(2)} mi / ${distanceKm.toFixed(2)} km`,
      pace: `${formatTime(pacePerMile)}/mi | ${formatTime(pacePerKm)}/km`,
      speed: `${speedMph.toFixed(2)} mph | ${speedKph.toFixed(2)} kph`
    });
  };

  const handleBasicClear = () => {
    setBasicInputs({
      time: '',
      distance: '',
      distanceUnit: 'miles',
      pace: '',
      paceUnit: 'min-per-mile',
      event: ''
    });
    setBasicResults(null);
  };

  // Multipoint calculator
  const handleMultipointCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const points = multipointInputs
      .filter(p => p.distance && p.time)
      .map(p => ({
        distance: convertToMiles(parseFloat(p.distance), p.unit),
        time: parseTime(p.time)
      }));

    if (points.length < 2) {
      alert(t('errorTwoPoints'));
      return;
    }

    const segments = [];
    for (let i = 1; i < points.length; i++) {
      const segmentDistance = points[i].distance - points[i - 1].distance;
      const segmentTime = points[i].time - points[i - 1].time;
      const segmentPace = segmentTime / segmentDistance;

      let paceDisplay;
      if (multipointPaceUnit === 'min-per-mile') {
        paceDisplay = formatTime(segmentPace) + '/mi';
      } else if (multipointPaceUnit === 'min-per-km') {
        paceDisplay = formatTime(segmentPace / 1.60934) + '/km';
      } else if (multipointPaceUnit === 'mph') {
        paceDisplay = (3600 / segmentPace).toFixed(2) + ' mph';
      } else {
        paceDisplay = (3600 / segmentPace * 1.60934).toFixed(2) + ' kph';
      }

      segments.push({
        number: i,
        distance: segmentDistance.toFixed(2),
        pace: paceDisplay,
        time: formatTime(segmentTime)
      });
    }

    setMultipointResults(segments);
  };

  const handleMultipointClear = () => {
    setMultipointInputs([
      { distance: '', unit: 'kilometers', time: '' },
      { distance: '', unit: 'kilometers', time: '' },
      { distance: '', unit: 'kilometers', time: '' },
      { distance: '', unit: 'kilometers', time: '' },
      { distance: '', unit: 'kilometers', time: '' }
    ]);
    setMultipointResults(null);
  };

  // Converter
  const handleConverterCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    let paceSeconds;

    if (converterInputs.fromUnit === 'mph') {
      const speed = parseFloat(converterInputs.fromValue);
      paceSeconds = 3600 / speed;
    } else if (converterInputs.fromUnit === 'kph') {
      const speed = parseFloat(converterInputs.fromValue);
      paceSeconds = 3600 / (speed * 0.621371);
    } else if (converterInputs.fromUnit === 'min-per-mile') {
      paceSeconds = parseTime(converterInputs.fromValue);
    } else {
      paceSeconds = parseTime(converterInputs.fromValue) * 1.60934;
    }

    let result;
    if (converterInputs.toUnit === 'mph') {
      result = (3600 / paceSeconds).toFixed(2) + ' mph';
    } else if (converterInputs.toUnit === 'kph') {
      result = (3600 / paceSeconds * 1.60934).toFixed(2) + ' kph';
    } else if (converterInputs.toUnit === 'min-per-mile') {
      result = formatTime(paceSeconds) + '/mi';
    } else {
      result = formatTime(paceSeconds / 1.60934) + '/km';
    }

    setConverterResult(result);
  };

  const handleConverterClear = () => {
    setConverterInputs({
      fromValue: '',
      fromUnit: 'min-per-mile',
      toUnit: 'min-per-km'
    });
    setConverterResult('');
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '100%', margin: '0 auto' },
    {},
    { maxWidth: '700px' },
    { maxWidth: '900px' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '1.5rem' },
    {},
    { marginBottom: '2rem' },
    { marginBottom: '2rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: '#1e40af', marginBottom: '0.5rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const tabsContainerStyle = getResponsiveStyle(
    { display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' as const, justifyContent: 'center' as const },
    {},
    { marginBottom: '2rem' },
    { marginBottom: '2rem' }
  );

  const tabButtonStyle = (isActive: boolean) => getResponsiveStyle(
    {
      background: isActive ? '#2563eb' : 'white',
      color: isActive ? 'white' : '#6b7280',
      border: `2px solid ${isActive ? '#2563eb' : '#e5e7eb'}`,
      borderRadius: '0.5rem',
      cursor: 'pointer',
      fontWeight: 500,
      transition: 'all 0.3s ease'
    },
    { padding: '0.625rem 1rem', fontSize: '0.875rem' },
    { padding: '0.75rem 1.5rem', fontSize: '0.95rem' },
    { padding: '0.75rem 1.5rem', fontSize: '0.95rem' }
  );

  const panelStyle = (isActive: boolean) => ({
    display: isActive ? 'block' : 'none',
    background: 'white',
    borderRadius: '1rem',
    padding: screenSize === 'mobile' ? '1.5rem' : '2rem',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
  });

  const formRowStyle = getResponsiveStyle(
    { marginBottom: '1.5rem' },
    { display: 'flex', flexDirection: 'column' as const, gap: '0.5rem' },
    { display: 'grid', gridTemplateColumns: '120px 1fr', gap: '1rem', alignItems: 'center' as const },
    { display: 'grid', gridTemplateColumns: '120px 1fr', gap: '1rem', alignItems: 'center' as const }
  );

  const formLabelStyle = {
    fontWeight: 600,
    color: '#374151',
    fontSize: screenSize === 'mobile' ? '0.875rem' : '1rem'
  };

  const inputGroupStyle = {
    display: 'flex',
    gap: '0.5rem',
    alignItems: 'center' as const,
    flexWrap: 'wrap' as const
  };

  const inputStyle = getResponsiveStyle(
    {
      padding: '0.75rem',
      border: '2px solid #e5e7eb',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      transition: 'border-color 0.3s ease',
      outline: 'none'
    },
    { width: '100%' },
    { width: '150px' },
    { width: '150px' }
  );

  const selectStyle = getResponsiveStyle(
    {
      padding: '0.75rem',
      border: '2px solid #e5e7eb',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      background: 'white',
      cursor: 'pointer'
    },
    { width: '100%' },
    { minWidth: '150px' },
    { minWidth: '150px' }
  );

  const hintTextStyle = {
    color: '#6b7280',
    fontSize: '0.875rem',
    marginLeft: screenSize === 'mobile' ? '0' : '0.5rem'
  };

  const buttonGroupStyle = {
    display: 'flex',
    gap: '1rem',
    justifyContent: 'center' as const,
    marginTop: '2rem',
    flexWrap: 'wrap' as const
  };

  const calculateButtonStyle = getResponsiveStyle(
    {
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white'
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem', width: '100%' },
    { padding: '0.875rem 2rem', fontSize: '1rem', width: 'auto' },
    { padding: '0.875rem 2rem', fontSize: '1rem', width: 'auto' }
  );

  const clearButtonStyle = getResponsiveStyle(
    {
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: '#f3f4f6',
      color: '#374151'
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem', width: '100%' },
    { padding: '0.875rem 2rem', fontSize: '1rem', width: 'auto' },
    { padding: '0.875rem 2rem', fontSize: '1rem', width: 'auto' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      marginTop: '2rem',
      background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
      borderRadius: '0.75rem',
      borderLeft: '4px solid #f59e0b'
    },
    { padding: '1.5rem' },
    { padding: '1.5rem' },
    { padding: '1.5rem' }
  );

  const resultGridStyle = getResponsiveStyle(
    { display: 'grid', gap: '1rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(2, 1fr)' },
    { gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }
  );

  const resultItemStyle = {
    background: 'white',
    padding: '1rem',
    borderRadius: '0.5rem',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
  };

  const resultLabelStyle = {
    display: 'block',
    color: '#6b7280',
    fontSize: '0.875rem',
    marginBottom: '0.25rem'
  };

  const resultValueStyle = getResponsiveStyle(
    { display: 'block', color: '#1e40af', fontWeight: 700 },
    { fontSize: '1.25rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.5rem' }
  );

  const noteTextStyle = {
    marginTop: '1rem',
    padding: '1rem',
    background: '#eff6ff',
    borderRadius: '0.5rem',
    color: '#1e40af',
    fontSize: '0.875rem',
    lineHeight: 1.6
  };

  const tableStyle = {
    width: '100%',
    marginBottom: '1.5rem',
    fontSize: screenSize === 'mobile' ? '0.875rem' : '1rem',
    overflowX: 'auto' as const
  };

  const glassCardStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(255, 200, 180, 0.2)',
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2.5rem', borderRadius: '1.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem', lineHeight: 1.7 },
    { fontSize: '1.125rem', lineHeight: 1.8 }
  );

  const listStyle = {
    marginLeft: '1.5rem',
    marginBottom: '1rem'
  };

  const listItemStyle = getResponsiveStyle(
    { color: colors.textSecondary, marginBottom: '0.5rem', lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={h2Style}>🏃 {t('toolTitle')}</h2>
          </div>

          <div style={tabsContainerStyle}>
            <button
              style={tabButtonStyle(activeTab === 'basic')}
              onClick={() => setActiveTab('basic')}
            >
              {t('tabBasic')}
            </button>
            <button
              style={tabButtonStyle(activeTab === 'multipoint')}
              onClick={() => setActiveTab('multipoint')}
            >
              {t('tabMultipoint')}
            </button>
            <button
              style={tabButtonStyle(activeTab === 'converter')}
              onClick={() => setActiveTab('converter')}
            >
              {t('tabConverter')}
            </button>
          </div>

          <div style={panelStyle(activeTab === 'basic')}>
            <form onSubmit={handleBasicCalculate}>
              <div style={formRowStyle}>
                <label style={formLabelStyle}>{t('labelTime')}</label>
                <div style={inputGroupStyle}>
                  <input
                    type="text"
                    style={inputStyle}
                    placeholder="00:30:00"
                    value={basicInputs.time}
                    onChange={(e) => setBasicInputs({ ...basicInputs, time: e.target.value })}
                  />
                  <span style={hintTextStyle}>{t('hintTime')}</span>
                </div>
              </div>

              <div style={formRowStyle}>
                <label style={formLabelStyle}>{t('labelDistance')}</label>
                <div style={inputGroupStyle}>
                  <input
                    type="number"
                    style={inputStyle}
                    placeholder="5"
                    step="0.01"
                    value={basicInputs.distance}
                    onChange={(e) => setBasicInputs({ ...basicInputs, distance: e.target.value })}
                  />
                  <select
                    style={selectStyle}
                    value={basicInputs.distanceUnit}
                    onChange={(e) => setBasicInputs({ ...basicInputs, distanceUnit: e.target.value })}
                  >
                    <option value="miles">{t('unitMiles')}</option>
                    <option value="kilometers">{t('unitKilometers')}</option>
                    <option value="meters">{t('unitMeters')}</option>
                    <option value="yards">{t('unitYards')}</option>
                  </select>
                  <select
                    style={selectStyle}
                    value={basicInputs.event}
                    onChange={(e) => handleEventChange(e.target.value)}
                  >
                    <option value="">{t('eventSelectPrompt')}</option>
                    <option value="marathon">{t('eventMarathon')}</option>
                    <option value="half-marathon">{t('eventHalfMarathon')}</option>
                    <option value="10k">{t('event10k')}</option>
                    <option value="5k">{t('event5k')}</option>
                    <option value="1k">{t('event1k')}</option>
                  </select>
                </div>
              </div>

              <div style={formRowStyle}>
                <label style={formLabelStyle}>{t('labelPace')}</label>
                <div style={inputGroupStyle}>
                  <input
                    type="text"
                    style={inputStyle}
                    placeholder="00:06:00"
                    value={basicInputs.pace}
                    onChange={(e) => setBasicInputs({ ...basicInputs, pace: e.target.value })}
                  />
                  <select
                    style={selectStyle}
                    value={basicInputs.paceUnit}
                    onChange={(e) => setBasicInputs({ ...basicInputs, paceUnit: e.target.value })}
                  >
                    <option value="min-per-mile">{t('pacePerMile')}</option>
                    <option value="min-per-km">{t('pacePerKm')}</option>
                    <option value="mph">{t('paceMph')}</option>
                    <option value="kph">{t('paceKph')}</option>
                  </select>
                  <span style={hintTextStyle}>
                    {basicInputs.paceUnit === 'mph' || basicInputs.paceUnit === 'kph' ? t('hintSpeed') : t('hintTime')}
                  </span>
                </div>
              </div>

              <div style={buttonGroupStyle}>
                <button type="submit" style={calculateButtonStyle}>{t('buttonCalculate')}</button>
                <button type="button" style={clearButtonStyle} onClick={handleBasicClear}>{t('buttonClear')}</button>
              </div>
            </form>

            {basicResults && (
              <div style={resultsStyle}>
                <h3 style={{ color: '#92400e', marginBottom: '1rem', fontSize: '1.25rem' }}>📊 {t('resultsTitle')}</h3>
                <div style={resultGridStyle}>
                  <div style={resultItemStyle}>
                    <span style={resultLabelStyle}>{t('resultTime')}</span>
                    <span style={resultValueStyle}>{basicResults.time}</span>
                  </div>
                  <div style={resultItemStyle}>
                    <span style={resultLabelStyle}>{t('resultDistance')}</span>
                    <span style={resultValueStyle}>{basicResults.distance}</span>
                  </div>
                  <div style={resultItemStyle}>
                    <span style={resultLabelStyle}>{t('resultPace')}</span>
                    <span style={resultValueStyle}>{basicResults.pace}</span>
                  </div>
                  <div style={resultItemStyle}>
                    <span style={resultLabelStyle}>{t('resultSpeed')}</span>
                    <span style={resultValueStyle}>{basicResults.speed}</span>
                  </div>
                </div>
                <div style={noteTextStyle}>
                  💡 <strong>{t('tipLabel')}</strong> {t('tipText')}
                </div>
              </div>
            )}
          </div>

          <div style={panelStyle(activeTab === 'multipoint')}>
            <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>{t('multipointDescription')}</p>

            <form onSubmit={handleMultipointCalculate}>
              <div style={{ overflowX: 'auto' }}>
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      <th style={{ background: '#f3f4f6', padding: '0.75rem', textAlign: 'left', fontWeight: 600, color: '#374151', width: '50px' }}>#</th>
                      <th style={{ background: '#f3f4f6', padding: '0.75rem', textAlign: 'left', fontWeight: 600, color: '#374151' }}>{t('tableDistance')}</th>
                      <th style={{ background: '#f3f4f6', padding: '0.75rem', textAlign: 'left', fontWeight: 600, color: '#374151' }}>{t('tableUnit')}</th>
                      <th style={{ background: '#f3f4f6', padding: '0.75rem', textAlign: 'left', fontWeight: 600, color: '#374151' }}>{t('tableTime')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {multipointInputs.map((input, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid #e5e7eb' }}>
                        <td style={{ padding: '0.5rem' }}>{idx + 1}</td>
                        <td style={{ padding: '0.5rem' }}>
                          <input
                            type="number"
                            style={{ ...inputStyle, width: screenSize === 'mobile' ? '80px' : '100px', fontSize: screenSize === 'mobile' ? '0.875rem' : '1rem' }}
                            step="0.01"
                            value={input.distance}
                            onChange={(e) => {
                              const newInputs = [...multipointInputs];
                              newInputs[idx].distance = e.target.value;
                              setMultipointInputs(newInputs);
                            }}
                          />
                        </td>
                        <td style={{ padding: '0.5rem' }}>
                          <select
                            style={{ ...selectStyle, width: screenSize === 'mobile' ? '80px' : 'auto', fontSize: screenSize === 'mobile' ? '0.875rem' : '1rem' }}
                            value={input.unit}
                            onChange={(e) => {
                              const newInputs = [...multipointInputs];
                              newInputs[idx].unit = e.target.value;
                              setMultipointInputs(newInputs);
                            }}
                          >
                            <option value="kilometers">{t('unitKilometers')}</option>
                            <option value="miles">{t('unitMiles')}</option>
                            <option value="meters">{t('unitMeters')}</option>
                            <option value="yards">{t('unitYards')}</option>
                          </select>
                        </td>
                        <td style={{ padding: '0.5rem' }}>
                          <input
                            type="text"
                            style={{ ...inputStyle, width: screenSize === 'mobile' ? '100px' : '120px', fontSize: screenSize === 'mobile' ? '0.875rem' : '1rem' }}
                            placeholder="00:05:00"
                            value={input.time}
                            onChange={(e) => {
                              const newInputs = [...multipointInputs];
                              newInputs[idx].time = e.target.value;
                              setMultipointInputs(newInputs);
                            }}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div style={formRowStyle}>
                <label style={formLabelStyle}>{t('labelPreferredPace')}</label>
                <select
                  style={selectStyle}
                  value={multipointPaceUnit}
                  onChange={(e) => setMultipointPaceUnit(e.target.value)}
                >
                  <option value="min-per-mile">{t('pacePerMile')}</option>
                  <option value="min-per-km">{t('pacePerKm')}</option>
                  <option value="mph">{t('paceMph')}</option>
                  <option value="kph">{t('paceKph')}</option>
                </select>
              </div>

              <div style={buttonGroupStyle}>
                <button type="submit" style={calculateButtonStyle}>{t('buttonCalculateSegments')}</button>
                <button type="button" style={clearButtonStyle} onClick={handleMultipointClear}>{t('buttonClear')}</button>
              </div>
            </form>

            {multipointResults && (
              <div style={resultsStyle}>
                <h3 style={{ color: '#92400e', marginBottom: '1rem', fontSize: '1.25rem' }}>📊 {t('segmentAnalysisTitle')}</h3>
                <div style={resultGridStyle}>
                  {multipointResults.map((segment: any) => (
                    <div key={segment.number} style={resultItemStyle}>
                      <span style={resultLabelStyle}>{t('segmentLabel')} {segment.number} ({segment.distance} mi)</span>
                      <span style={resultValueStyle}>{segment.pace}</span>
                      <span style={{ ...resultLabelStyle, marginTop: '0.5rem' }}>{t('segmentTime')}: {segment.time}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div style={panelStyle(activeTab === 'converter')}>
            <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>{t('converterDescription')}</p>

            <form onSubmit={handleConverterCalculate}>
              <div style={formRowStyle}>
                <label style={formLabelStyle}>{t('labelFrom')}</label>
                <div style={inputGroupStyle}>
                  <input
                    type="text"
                    style={inputStyle}
                    placeholder="6:00"
                    value={converterInputs.fromValue}
                    onChange={(e) => setConverterInputs({ ...converterInputs, fromValue: e.target.value })}
                  />
                  <select
                    style={selectStyle}
                    value={converterInputs.fromUnit}
                    onChange={(e) => setConverterInputs({ ...converterInputs, fromUnit: e.target.value })}
                  >
                    <option value="min-per-mile">{t('pacePerMile')}</option>
                    <option value="min-per-km">{t('pacePerKm')}</option>
                    <option value="mph">{t('paceMph')}</option>
                    <option value="kph">{t('paceKph')}</option>
                  </select>
                  <span style={hintTextStyle}>
                    {converterInputs.fromUnit === 'mph' || converterInputs.fromUnit === 'kph' ? t('hintSpeed') : t('hintTimeShort')}
                  </span>
                </div>
              </div>

              <div style={formRowStyle}>
                <label style={formLabelStyle}>{t('labelTo')}</label>
                <div style={inputGroupStyle}>
                  <select
                    style={selectStyle}
                    value={converterInputs.toUnit}
                    onChange={(e) => setConverterInputs({ ...converterInputs, toUnit: e.target.value })}
                  >
                    <option value="min-per-mile">{t('pacePerMile')}</option>
                    <option value="min-per-km">{t('pacePerKm')}</option>
                    <option value="mph">{t('paceMph')}</option>
                    <option value="kph">{t('paceKph')}</option>
                  </select>
                </div>
              </div>

              <div style={buttonGroupStyle}>
                <button type="submit" style={calculateButtonStyle}>{t('buttonConvert')}</button>
                <button type="button" style={clearButtonStyle} onClick={handleConverterClear}>{t('buttonClear')}</button>
              </div>
            </form>

            {converterResult && (
              <div style={resultsStyle}>
                <h3 style={{ color: '#92400e', marginBottom: '1rem', fontSize: '1.25rem' }}>🔄 {t('conversionResultTitle')}</h3>
                <div style={{ ...resultItemStyle, textAlign: 'center' }}>
                  <span style={{ ...resultValueStyle, fontSize: screenSize === 'mobile' ? '1.5rem' : '2rem' }}>{converterResult}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>{t('howToStep1')}</li>
          <li style={listItemStyle}>{t('howToStep2')}</li>
          <li style={listItemStyle}>{t('howToStep3')}</li>
          <li style={listItemStyle}>{t('howToStep4')}</li>
          <li style={listItemStyle}>{t('howToStep5')}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t('insight1')}</li>
          <li style={listItemStyle}>{t('insight2')}</li>
          <li style={listItemStyle}>{t('insight3')}</li>
          <li style={listItemStyle}>{t('insight4')}</li>
          <li style={listItemStyle}>{t('insight5')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>
        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem' }}>{t('detailsSubtitle1')}</h3>
        <p style={paragraphStyle}>{t('detailsParagraph1')}</p>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem' }}>{t('detailsSubtitle2')}</h3>
        <p style={paragraphStyle}>{t('detailsParagraph2')}</p>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem' }}>{t('detailsSubtitle3')}</h3>
        <p style={paragraphStyle}>{t('detailsParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginBottom: '0.5rem' }}>{t('faq1Question')}</h3>
          <p style={paragraphStyle}>{t('faq1Answer')}</p>
        </div>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginBottom: '0.5rem' }}>{t('faq2Question')}</h3>
          <p style={paragraphStyle}>{t('faq2Answer')}</p>
        </div>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginBottom: '0.5rem' }}>{t('faq3Question')}</h3>
          <p style={paragraphStyle}>{t('faq3Answer')}</p>
        </div>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginBottom: '0.5rem' }}>{t('faq4Question')}</h3>
          <p style={paragraphStyle}>{t('faq4Answer')}</p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}>
            <a href="https://triagemethod.com/triage-pace-calculator/" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'underline' }}>
              {t('reference1')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.calculator.net/pace-calculator.html" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'underline' }}>
              {t('reference2')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://runbundle.com/tools/weight-vs-pace-calculator" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'underline' }}>
              {t('reference3')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.healthline.com/health/exercise-fitness/average-walking-speed" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'underline' }}>
              {t('reference4')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.puregym.com/blog/running-pace-guide/" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'underline' }}>
              {t('reference5')}
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}